import {
	checkIfImage,
	CoreCaretIcon,
	CoreErrorIndicator,
	CoreFileCard,
	CoreFileItem,
	CoreFilePreview,
	CoreIconButton,
	CoreLayoutSlider,
	CoreNoResults,
	CorePaper,
	CoreSkeletonWrapper,
	CoreTextField,
	CoreXIcon,
} from "@verity/components/core";
import { UseAssignmentResultReturn } from "../hooks/useAssignmentResult";
import { Dialog, DialogContent, ImageList, ImageListItem, Typography } from "@mui/material";
import i18n from "../../../../../i18n";
import styles from "./AssignmentPanel.module.css";
import { useId, useState } from "react";
import useCarousel, { CarouselControl } from "../../../../../portal/components/upload-files/useCarousel";
import ZoomInIcon from "../../../../../images/portal/zoom-in.svg?react";
import ZoomOutIcon from "../../../../../images/portal/zoom-out.svg?react";
import FullScreenIcon from "../../../../../images/portal/fullscreen-square.svg?react";
import { UseVerityAssessmentResultReturn } from "../hooks/useVerityAssessmentResult";
import {
	DraftGrade,
	DraftGradeError,
	getAssignmentQuestion,
	getDraftGradeErrorMessage,
	getMaxGradeForQuestion,
	isGradeable,
} from "../hooks/verityAssessmentResultHelper";
import { useMobileLayout } from "@verity/components";

type GradeInputProps = {
	value: string;
	onChange: (value: string) => void;
	ariaLabelledBy: string;
	maxGrade: number;
	error?: DraftGradeError;
};

export function GradeInput({ value, onChange, ariaLabelledBy, maxGrade, error }: GradeInputProps) {
	const errorMessage = getDraftGradeErrorMessage(error);
	return (
		<div className={styles.gradeInputContainer}>
			{errorMessage && <CoreErrorIndicator error={errorMessage} size={18} />}
			<CoreTextField
				value={value}
				onChange={(e) => onChange(e.target.value)}
				size="small"
				className={styles.gradeInput}
				ariaLabelledBy={ariaLabelledBy}
				error={Boolean(error)}
			/>
			<Typography className={styles.totalPoints}>/{maxGrade}</Typography>
		</div>
	);
}

type NotesTextFieldProps = {
	value: string;
	onChange: (value: string) => void;
};

export function NotesTextField({ value, onChange }: NotesTextFieldProps) {
	return (
		<CoreTextField
			multiline
			minRows={2}
			maxRows={4}
			label={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.writeNotePlaceholder") as string}
			value={value}
			onChange={(e) => onChange(e.target.value)}
		/>
	);
}

type TopCarouselControlProps = CarouselControl<CoreFileItem> & {
	totalItems: number;
	value: number;
	onChange: (value: number) => void;
};

function TopCarouselControl(props: TopCarouselControlProps) {
	const { backControl, nextControl, inputControl, totalItems } = props;

	const showSlider = totalItems > 1;
	const showCarouselButtons = props.value === 1 && totalItems > 1;

	return (
		<div className={styles.topControlContainer}>
			{showSlider && (
				<div className={styles.sideBlock}>
					<CoreLayoutSlider value={props.value} onChange={props.onChange} />
				</div>
			)}
			{showCarouselButtons && (
				<div className={styles.topCarouselControlContainer}>
					<CoreIconButton
						variant="plain"
						className={styles.carouselButton}
						onClick={backControl.handle}
						aria-label={backControl.ariaLabel}
						disabled={backControl.disabled}
					>
						<CoreCaretIcon direction="left" color="#5C5C5C" />
					</CoreIconButton>
					<CoreTextField
						type="number"
						{...inputControl}
						className={styles.carouselIndexInput}
						size="small"
						ariaLabel={
							i18n.t("result.components.resultsPage.proctoringResultsViewTSX.goToFile", {
								total: totalItems,
							}) as string
						}
					/>
					<Typography className={styles.totalPoints}>{`/ ${totalItems}`}</Typography>
					<CoreIconButton
						variant="plain"
						className={styles.carouselButton}
						onClick={nextControl.handle}
						aria-label={nextControl.ariaLabel}
						disabled={nextControl.disabled}
					>
						<CoreCaretIcon direction="right" color="#5C5C5C" />
					</CoreIconButton>
				</div>
			)}
			{showSlider && <div className={styles.sideBlock} aria-hidden="true" />}
		</div>
	);
}

type ImagePreviewProps = Pick<CarouselControl<CoreFileItem>, "currentItem"> &
	Pick<UseAssignmentResultReturn, "fetch"> & {
		onFullScreen: () => void;
	};

function ImagePreview({ currentItem, fetch, onFullScreen }: ImagePreviewProps) {
	return (
		<CoreFilePreview
			file={currentItem}
			slotProps={{
				container: { className: styles.imagePreviewContainer },
				imageWrapper: { className: styles.imagePreviewWrapper },
			}}
			renderOverlay={() => (
				<CoreIconButton
					variant="plain"
					className={styles.fullScreenButton}
					onClick={onFullScreen}
					aria-haspopup="dialog"
					aria-label={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.fullScreenAriaLabel") as string}
				>
					<FullScreenIcon />
				</CoreIconButton>
			)}
		>
			<CoreFileCard showDownload file={currentItem} onRefetch={() => fetch.student(currentItem.name)} />
		</CoreFilePreview>
	);
}

const MAX_FILES_MOBILE = 1;
const MAX_FILES = 5;

type BottomCarouselControlProps = CarouselControl<CoreFileItem> & {
	files: CoreFileItem[];
};

function BottomCarouselControl(props: BottomCarouselControlProps) {
	const { backControl, nextControl, hasMultipleItems, files, goToIndex, currentIndex } = props;
	const isMobileLayout = useMobileLayout();
	const maxFiles = isMobileLayout ? MAX_FILES_MOBILE : MAX_FILES;

	const startIndex = Math.max(0, Math.min(currentIndex - Math.floor(maxFiles / 2), files.length - maxFiles));

	const adjustedStart = files.length <= maxFiles ? 0 : startIndex;
	const visibleFiles = files.slice(adjustedStart, adjustedStart + maxFiles);

	return (
		<div className={styles.bottomCarouselControlContainer}>
			{hasMultipleItems && (
				<CoreIconButton
					variant="plain"
					className={styles.carouselButton}
					onClick={backControl.handle}
					aria-label={backControl.ariaLabel}
					disabled={backControl.disabled}
				>
					<CoreCaretIcon direction="left" color="#5C5C5C" />
				</CoreIconButton>
			)}
			{visibleFiles.map((file, index) => {
				const absoluteIndex = adjustedStart + index;
				const isSelected = absoluteIndex === currentIndex;
				return (
					<CoreFilePreview
						key={absoluteIndex}
						onImgClick={() => goToIndex(absoluteIndex)}
						file={file}
						slotProps={{
							container: {
								"aria-current": isSelected ? "true" : undefined,
								className: `${styles.thumbnailButton} ${isSelected ? styles.selected : ""}`,
								"aria-label": i18n.t("result.components.resultsPage.proctoringResultsViewTSX.previewImage", {
									fileName: file.name,
								}) as string,
							},
							image: {
								className: styles.thumbnailImage,
							},
							fileName: {
								hide: true,
							},
						}}
					/>
				);
			})}
			{hasMultipleItems && (
				<CoreIconButton
					variant="plain"
					className={styles.carouselButton}
					onClick={nextControl.handle}
					aria-label={nextControl.ariaLabel}
					disabled={nextControl.disabled}
				>
					<CoreCaretIcon direction="right" color="#5C5C5C" />
				</CoreIconButton>
			)}
		</div>
	);
}

type ImageGridProps = {
	gridColumns: number;
	files: CoreFileItem[];
	onClickFile: (index: number) => void;
};

function ImageGrid({ gridColumns, files, onClickFile }: ImageGridProps) {
	return (
		<ImageList cols={gridColumns} className={styles.imageGrid} rowHeight={"auto"} gap={16}>
			{files.map((file, index) => (
				<ImageListItem key={index}>
					<CoreFilePreview
						onImgClick={() => onClickFile(index)}
						file={file}
						slotProps={{
							container: { className: styles.imgGridContainer, "aria-haspopup": "dialog" },
							fallbackContainer: { className: styles.imgGridFallbackContainer },
							imageWrapper: { className: styles.imgGridImageWrapper },
						}}
					/>
				</ImageListItem>
			))}
		</ImageList>
	);
}

const MAX_ZOOM = 3;
const MIN_ZOOM = 0.5;

type FullScreenCarouselProps = CarouselControl<CoreFileItem> & {
	onClose: () => void;
	totalItems: number;
};

function FullScreenCarousel(props: FullScreenCarouselProps) {
	const { onClose, totalItems, ...carouselControls } = props;
	const { backControl, nextControl, currentItem, inputControl } = carouselControls;
	const showCarouselButtons = totalItems > 1;
	const isImage = checkIfImage(currentItem.name);

	const [zoom, setZoom] = useState(1);

	const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.25, MAX_ZOOM));
	const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.25, MIN_ZOOM));

	return (
		<Dialog
			open
			fullScreen
			onClose={onClose}
			PaperProps={{ sx: { backgroundColor: "#212121E5", padding: 0 }, "aria-modal": "true" }}
		>
			<DialogContent className={styles.carouselDialogContent}>
				<div className={styles.fullScreenTopRow}>
					<div className={styles.buttonRow} aria-hidden="true" />
					{showCarouselButtons && (
						<div className={styles.fullScreenCarouselControlContainer}>
							<CoreIconButton
								variant="plain"
								color="dark"
								className={styles.carouselButton}
								onClick={backControl.handle}
								aria-label={backControl.ariaLabel}
								disabled={backControl.disabled}
							>
								<CoreCaretIcon direction="left" color="#fff" />
							</CoreIconButton>
							<CoreTextField
								type="number"
								{...inputControl}
								className={styles.carouselIndexInput}
								size="small"
								colorVariant="dark"
								ariaLabel={
									i18n.t("result.components.resultsPage.proctoringResultsViewTSX.goToFile", {
										total: totalItems,
									}) as string
								}
							/>
							<Typography className={styles.totalPoints}>{`/ ${totalItems}`}</Typography>
							<CoreIconButton
								variant="plain"
								color="dark"
								className={styles.carouselButton}
								onClick={nextControl.handle}
								aria-label={nextControl.ariaLabel}
								disabled={nextControl.disabled}
							>
								<CoreCaretIcon direction="right" color="#fff" />
							</CoreIconButton>
						</div>
					)}
					<div className={styles.buttonRow}>
						{isImage && (
							<>
								<CoreIconButton
									variant="plain"
									onClick={handleZoomOut}
									color="dark"
									className={styles.fsButton}
									disabled={zoom <= MIN_ZOOM}
									tooltip={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.zoomOutAriaLabel") as string}
								>
									<ZoomOutIcon />
								</CoreIconButton>
								<CoreIconButton
									variant="plain"
									onClick={handleZoomIn}
									color="dark"
									className={styles.fsButton}
									disabled={zoom >= MAX_ZOOM}
									tooltip={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.zoomInAriaLabel") as string}
								>
									<ZoomInIcon />
								</CoreIconButton>{" "}
							</>
						)}
						<CoreIconButton
							variant="plain"
							onClick={onClose}
							color="dark"
							className={styles.fsButton}
							aria-label={
								i18n.t("result.components.resultsPage.proctoringResultsViewTSX.closeFullScreenAriaLabel") as string
							}
						>
							<CoreXIcon />
						</CoreIconButton>
					</div>
				</div>

				<div className={styles.imageContainer}>
					<CoreFilePreview
						file={currentItem}
						slotProps={{
							container: { className: styles.fsImagePreviewWrapper },
							imageWrapper: { className: styles.fsImageWrapper },
							image: {
								className: styles.fsImage,
								style: {
									width: `${100 * zoom}%`,
									height: `${100 * zoom}%`,
									maxWidth: "none",
									maxHeight: "none",
								},
							},
							fileName: {
								className: styles.fsFallbackText,
							},
						}}
					/>
				</div>
			</DialogContent>
		</Dialog>
	);
}

type AssignmentPanelProps = UseAssignmentResultReturn &
	Pick<UseVerityAssessmentResultReturn, "grade" | "quizQuestions"> & {
		draft: DraftGrade;
		responseId: number;
	};

export default function AssignmentPanel(props: AssignmentPanelProps) {
	const question = getAssignmentQuestion(props.quizQuestions);

	const files = props.files.student;
	const loading = props.loading;

	const [gridColumns, setGridColumns] = useState(1);
	const [fullScreenShowing, setFullScreenShowing] = useState(false);

	const carouselControls = useCarousel<CoreFileItem>(files, 0, false);
	const gradeControl = props.grade;
	const gradeInputId = useId();
	const maxGrade = getMaxGradeForQuestion(question);
	const responseId = props.responseId;

	return (
		<div className={styles.assignmentPanelContainer}>
			<CorePaper className={styles.assignmentPaper}>
				<CoreSkeletonWrapper loading={loading} height={350}>
					<Typography className={styles.assignmentTitle} variant="h2">
						{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.answer")}
					</Typography>
					<CorePaper className={styles.studentAnswerContainer}>
						{files.length > 0 && (
							<>
								<TopCarouselControl
									{...carouselControls}
									totalItems={files.length}
									value={gridColumns}
									onChange={setGridColumns}
								/>
								{gridColumns === 1 && (
									<>
										<ImagePreview
											currentItem={carouselControls.currentItem}
											fetch={props.fetch}
											onFullScreen={() => setFullScreenShowing(true)}
										/>
										<BottomCarouselControl {...carouselControls} files={files} />
									</>
								)}
								{gridColumns > 1 && (
									<ImageGrid
										gridColumns={gridColumns}
										files={files}
										onClickFile={(index) => {
											carouselControls.goToIndex(index);
											setFullScreenShowing(true);
										}}
									/>
								)}
							</>
						)}
						{files.length === 0 && (
							<CoreNoResults
								header={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.noFiles") as string}
								variant="no-files"
							/>
						)}
					</CorePaper>
				</CoreSkeletonWrapper>
				<CoreSkeletonWrapper loading={loading} height={120}>
					<div className={styles.gradingSection}>
						{isGradeable(question) && (
							<div className={styles.gradingContainer}>
								<div className={styles.gradingHeader}>
									<Typography variant="h2" className={styles.gradingTitle}>
										{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.gradedByPoints")}
									</Typography>
									<Typography className={styles.gradingDesc} id={gradeInputId}>
										{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.gradedByPointsDesc")}
									</Typography>
								</div>
								<GradeInput
									value={props.draft.score}
									onChange={(value) => gradeControl.onChange(responseId, "score", value)}
									maxGrade={maxGrade}
									error={props.draft.error}
									ariaLabelledBy={gradeInputId}
								/>
							</div>
						)}
						<NotesTextField
							value={props.draft.notes}
							onChange={(value) => gradeControl.onChange(responseId, "notes", value)}
						/>
					</div>
				</CoreSkeletonWrapper>
			</CorePaper>
			{fullScreenShowing && carouselControls.currentItem && (
				<FullScreenCarousel
					{...carouselControls}
					onClose={() => setFullScreenShowing(false)}
					totalItems={files.length}
				/>
			)}
		</div>
	);
}
